/**
 * BM-H4253 (13 Oct 2026): "Connect Claude to CaseMinder".
 *
 * Vanessa: "can we have an API with CaseMinder whereby Claude can post time
 * for me and so on?" When someone adds the CaseMinder connector in Claude,
 * Claude sends them here (via /api/mcp/oauth/authorize, which parks the
 * request and hands over its id). The page sits behind the normal sign-in, so
 * the person is signed in to CaseMinder as themselves when they press Allow,
 * and only then does Claude get a code (backend/hub/caseminder_mcp_oauth.py).
 *
 * Opened without a request, it is where a person sees whether Claude is
 * connected and can disconnect it.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Bot, ShieldCheck } from "lucide-react";
import { API } from "../config/api";

const card = "bg-white/[0.02] border border-white/10 rounded-lg p-5 mb-6";

function errorText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d.message === "string") return d.message;
  return fallback;
}

export default function ConnectClaude() {
  const [params] = useSearchParams();
  const requestId = params.get("request") || "";
  const [ask, setAsk] = useState(null);
  const [askError, setAskError] = useState("");
  const [busy, setBusy] = useState(false);
  const [connections, setConnections] = useState(null);
  const [connError, setConnError] = useState("");
  const [notice, setNotice] = useState("");

  const loadConnections = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mcp/oauth/connections`);
      setConnections(r.data.connections || []);
      setConnError("");
    } catch (e) {
      setConnections(null);
      setConnError(errorText(e, "Your Claude connections could not be loaded."));
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    if (!requestId) return;
    axios
      .get(`${API}/mcp/oauth/requests/${encodeURIComponent(requestId)}`)
      .then((r) => setAsk(r.data))
      .catch((e) => setAskError(errorText(e, "This connection request could not be loaded. Start again from Claude.")));
  }, [requestId]);

  const decide = async (approve) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mcp/oauth/requests/${encodeURIComponent(requestId)}/decision`, { approve });
      window.location.assign(r.data.redirect_to);
    } catch (e) {
      setAskError(errorText(e, "That did not go through. Start again from Claude."));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mcp/oauth/disconnect`);
      setNotice(`Claude is disconnected (${r.data.revoked_tokens} sign-in tokens cancelled).`);
      await loadConnections();
    } catch (e) {
      setNotice(errorText(e, "Claude could not be disconnected. Try again."));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen" style={{ background: "#060e18" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f9fafb", margin: "0 0 20px" }} className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-teal" />
          Connect Claude to CaseMinder
        </h1>

        {requestId && (
          <section className={card} data-testid="connect-claude-request">
            {askError && <p className="text-sm text-red-300" data-testid="connect-claude-error">{askError}</p>}
            {!askError && !ask && <p className="text-sm text-slate-300">Loading the request...</p>}
            {!askError && ask && (
              <>
                <p className="text-white mb-3">
                  <strong>{ask.client_name}</strong> wants to connect to CaseMinder as{" "}
                  <strong data-testid="connect-claude-as">{ask.signed_in_as}</strong>.
                </p>
                <p className="text-sm text-slate-300 mb-1 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-teal" />
                  It will be able to: {ask.scope}.
                </p>
                <p className="text-sm text-slate-400 mb-4">
                  Everything it does is recorded in CaseMinder under your name. You can disconnect it here at any time.
                  After you allow it, you go back to {ask.returns_to}.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => decide(true)}
                    className="px-4 py-2 rounded bg-teal text-navy font-semibold text-sm disabled:opacity-50"
                    data-testid="connect-claude-allow"
                  >
                    Allow
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => decide(false)}
                    className="px-4 py-2 rounded border border-white/20 text-white text-sm disabled:opacity-50"
                    data-testid="connect-claude-decline"
                  >
                    Decline
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        <section className={card} data-testid="connect-claude-connections">
          <h2 className="text-base font-semibold text-white mb-3">Your Claude connections</h2>
          {connError && <p className="text-sm text-red-300">{connError}</p>}
          {connections && connections.length === 0 && (
            <p className="text-sm text-slate-300" data-testid="connect-claude-none">Claude is not connected to your CaseMinder account.</p>
          )}
          {connections && connections.length > 0 && (
            <>
              <ul className="text-sm text-slate-200 mb-4">
                {connections.map((c, i) => (
                  <li key={i} data-testid="connect-claude-connection">
                    {c.app}, connected {c.since ? new Date(c.since).toLocaleString("en-GB") : ""}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={busy}
                onClick={disconnect}
                className="px-4 py-2 rounded border border-red-400 text-red-300 text-sm disabled:opacity-50"
                data-testid="connect-claude-disconnect"
              >
                Disconnect Claude
              </button>
            </>
          )}
          {notice && <p className="text-sm text-slate-300 mt-3" data-testid="connect-claude-notice">{notice}</p>}
        </section>
      </div>
    </div>
  );
}
